<script setup lang="ts">
import { computed } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import ConfirmButton from './ConfirmButton.vue'
import type { ConfirmDialogProps, ConfirmForwardEmits } from './types'

interface Props extends ConfirmDialogProps {
  /** Label of the trigger button rendered in the banner. */
  triggerLabel: string
  /**
   * Render as a borderless row (title/description left, button right) instead of
   * a self-contained bordered card. Use inside a parent "danger zone" group that
   * already provides the border and row dividers.
   */
  flush?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  severity: 'info',
  mode: 'form',
  method: 'POST',
  requireConfirmation: false,
  size: 'sm',
  flush: false,
})

const emit = defineEmits<ConfirmForwardEmits>()

const { t } = useI18n()
const resolvedTitle = computed(() => t(props.title))
const resolvedDescription = computed(() => (props.description ? t(props.description) : ''))

const borderClass = computed(() => {
  switch (props.severity) {
    case 'danger':
      return 'tw:border-red-500'
    case 'success':
      return 'tw:border-green-500'
    case 'warning':
      return 'tw:border-yellow-500'
    case 'info':
      return 'tw:border-blue-500'
    default:
      return 'tw:border-gray-500'
  }
})

const wrapperClass = computed(() =>
  props.flush
    ? 'tw:flex tw:items-center tw:justify-between tw:gap-3 tw:p-4'
    : ['tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded tw:border tw:p-3 tw:md:flex-row', borderClass.value],
)

/** Forward every ConfirmDialog prop to the button; strip banner-only props. */
const buttonProps = reactiveOmit(props, 'triggerLabel', 'flush')
</script>

<template>
  <div :class="wrapperClass">
    <p class="tw:mr-auto tw:mb-0">
      <strong>{{ resolvedTitle }}</strong><br>
      <span class="tw:text-muted-foreground">{{ resolvedDescription }}</span>
    </p>
    <ConfirmButton
      v-bind="buttonProps"
      :label="triggerLabel"
      @confirmed="emit('confirmed')"
      @success="emit('success', $event)"
      @error="emit('error', $event)"
    />
  </div>
</template>
